import React from 'react';
import { Box, Button, Link } from '@mui/material';
import { EditOutlined as EditIcon, Telegram as TelegramIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../theme/tokens';
import { BOT_URL } from '../../config/telegram';
import FinMark from '../brand/FinMark';

interface DashboardEmptyStateProps {
  onAddTransaction: () => void;
  onConnectTelegram: () => void;
  onReplayTutorial: () => void;
  isTelegramLinked: boolean;
}

const EXAMPLES = ['almoco', 'uber', 'salary'] as const;

/**
 * The personal Início before the first entry: the two ways in — tell Fin on
 * Telegram, or fill it in here — and the tutorial one tap away.
 */
const DashboardEmptyState: React.FC<DashboardEmptyStateProps> = ({
  onAddTransaction,
  onConnectTelegram,
  onReplayTutorial,
  isTelegramLinked,
}) => {
  const { t } = useTranslation('dashboard');

  const card = {
    flex: '1 1 300px',
    minWidth: 0,
    p: { xs: 2.5, md: 3 },
    borderRadius: '20px',
    border: `1px solid ${tokens.line}`,
    bgcolor: tokens.surface,
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
  } as const;

  return (
    <Box component='section' aria-labelledby='empty-title' sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Box component='h2' id='empty-title' sx={{ m: 0, fontFamily: tokens.display, fontSize: { xs: 22, md: 26 }, fontWeight: 700, letterSpacing: '-0.02em' }}>
          {t('emptyState.title')}
        </Box>
        <Box component='p' sx={{ m: 0, color: tokens.ink2, maxWidth: 620 }}>{t('emptyState.subtitle')}</Box>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5 }}>
        <Box sx={{ ...card, bgcolor: tokens.accentSoft, borderColor: tokens.accentSoft2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <FinMark size={36} />
            <Box component='h3' sx={{ m: 0, fontFamily: tokens.display, fontSize: 18, fontWeight: 650 }}>{t('emptyState.fin.title')}</Box>
          </Box>
          <Box component='p' sx={{ m: 0, fontSize: 14, color: tokens.ink2, lineHeight: 1.5 }}>
            {isTelegramLinked ? t('emptyState.fin.linked') : t('emptyState.fin.notLinked')}
          </Box>
          <Box component='ul' aria-label={t('emptyState.fin.examples')} sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {EXAMPLES.map((example) => (
              <Box
                component='li'
                key={example}
                sx={{ px: 1.5, py: 0.75, borderRadius: '14px 14px 14px 4px', bgcolor: tokens.surface, fontSize: 14, border: `1px solid ${tokens.accentSoft2}` }}
              >
                {t(`emptyState.fin.example.${example}`)}
              </Box>
            ))}
          </Box>
          <Box sx={{ mt: 'auto', pt: 0.5 }}>
            {isTelegramLinked ? (
              <Button variant='contained' startIcon={<TelegramIcon />} href={BOT_URL} target='_blank' rel='noopener noreferrer' sx={{ minHeight: 44 }}>
                {t('emptyState.fin.open')}
              </Button>
            ) : (
              <Button variant='contained' startIcon={<TelegramIcon />} onClick={onConnectTelegram} sx={{ minHeight: 44 }}>
                {t('emptyState.fin.connect')}
              </Button>
            )}
          </Box>
        </Box>

        <Box sx={card}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box aria-hidden sx={{ width: 36, height: 36, borderRadius: '12px', bgcolor: tokens.bg, color: tokens.ink2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <EditIcon fontSize='small' />
            </Box>
            <Box component='h3' sx={{ m: 0, fontFamily: tokens.display, fontSize: 18, fontWeight: 650 }}>{t('emptyState.manual.title')}</Box>
          </Box>
          <Box component='p' sx={{ m: 0, fontSize: 14, color: tokens.ink2, lineHeight: 1.5 }}>{t('emptyState.manual.description')}</Box>
          <Box sx={{ mt: 'auto', pt: 0.5 }}>
            <Button variant='outlined' onClick={onAddTransaction} sx={{ minHeight: 44 }}>
              {t('emptyState.manual.button')}
            </Button>
          </Box>
        </Box>
      </Box>

      <Box component='p' sx={{ m: 0, fontSize: 14, color: tokens.ink3 }}>
        {t('emptyState.tutorialPrompt')}{' '}
        <Link component='button' type='button' onClick={onReplayTutorial} sx={{ fontSize: 14, fontWeight: 600, verticalAlign: 'baseline' }}>
          {t('emptyState.tutorialLink')}
        </Link>
      </Box>
    </Box>
  );
};

export default DashboardEmptyState;
